<template>
  <div v-if="dataLoaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/promotion`)"
          icon="keyboard_backspace"
        />
      </div>

      <div class="col-6">
        <q-btn
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="readOnly ? getString('action.edit') : getString('action.save')"
          @click="saveChanges()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-btn
          v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          @click="deletePromotion()"
          align="right"
        />
      </div>
    </div>

    <div class="row gutter-sm q-mb-lg">
      <!-- General Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString('general') }}
        </p>
        <div class="card-block">
          <!-- Title -->
          <q-field class="q-mb-md">
            <q-input
              type="text"
              v-model="data.title"
              :disable="readOnly"
              :float-label="getString('title')"
            >
            </q-input>
          </q-field>

          <!-- Publication date -->
          <q-datetime
            class="full-width q-mb-md"
            :disable="readOnly"
            :readonly="readOnly"
            :float-label="getString('module.blog.details.publication.date.datepicker.label')"
            v-model="data.datetime_online"
            type="datetime"
          />

          <!-- Books -->
          <chips-autocomplete
            class="q-pb-md"
            :disable="readOnly"
            :selection="data.projects"
            :name="'Books'"
            :labelTerm="'title'"
            :endpoint="'project'"
            :resultLabel="'projects'"
          />

          <!-- Relations -->
          <chips-autocomplete
            class="q-pb-md"
            :disable="readOnly"
            :selection="data.relations"
            :name="'Relations'"
            :labelTerm="'name'"
            :endpoint="'relation'"
            :resultLabel="'relations'"
          />
        </div>
      </div>
      <!-- Publication Information -->
      <div class="col-md-6 col-sm-12 col-xs-12">
        <p class="card-title">
          {{ getString(null, 'Image', 'Afbeelding') }}
        </p>
        <div class="card-block">
          <div class="row">
            <div class="col-md-8 q-mr-md">
              <div
                class="card-block image-preview image flex-center"
                :class="!data.image.location && 'card-block__error'"
              >
                <img
                  v-if="imgSrc"
                  class="image-preview__small centered"
                  :src="`${buildFileLocation(imgSrc)}?timestamp=${Date.now()}`"
                  alt=""
                />
              </div>
            </div>
            <div class="col">
              <q-btn
                class="capitalize ctrl-button"
                :color="readOnly ? 'grey-5' : 'primary'"
                :disabled="readOnly"
                :label="getString('editor.button.image.select')"
                @click="!readOnly && setModalContext()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- content editor -->
    <div class="row gutter-sm q-mb-lg q-mt-sm">
      <div class="full-width">
        <p class="card-title">
          {{ getString('block.type.html.content') }}
        </p>
        <div class="card-block scroll">
          <!-- Field: HTML Editor -->
          <tiny-editor
            tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
            :init="tinyConfig()"
            ref="tm"
            :disabled="readOnly"
            v-model="data.content"
          >
          </tiny-editor>
        </div>
      </div>
    </div>

    <!-- image select modal -->
    <q-modal v-if="imageModal" v-model="imageModal">
      <file-select-modal
        :fileType="'image'"
        :guid="`promotion-detail`"
        @close="imageModal = false"
      />
    </q-modal>
  </div>
</template>

<script>
import moment from 'moment';

import saveChanges from '@/mixins/detailViews/saveChanges';
import suggestUrl from '@/mixins/suggestUrl';
import getString from '@/helpers/GetStrings';

import EventBus from '@/event-bus';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';
import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';

const TinyEditor = require('@tinymce/tinymce-vue').default;

export default {
  data() {
    return {
      imageModal: false,
      successMessage: this.getString(
        null,
        'Successfully saved the promotion',
        'De promotie is succesvol opgeslagen',
      ),
      searchString: '',
      noCategory: null,
      categories: [],
      dataLoaded: false,
      endpoint: 'promotion',
      responseData: {
        multiple: 'promotions',
        single: 'promotion',
      },
      module: 'promotion',
      data: {
        content: '',
        title: '',
        projects: [],
        relations: [],
        image: {
          location: '',
        },
      },
    };
  },

  components: {
    TinyEditor,
    FileSelectModal,
    ChipsAutocomplete,
  },

  mixins: [suggestUrl, saveChanges],
  computed: {
    imgSrc() {
      return this.data.image && this.data.image.location;
    },
  },
  methods: {
    tinyConfig() {
      return {
        readOnly: false,
        height: 200,
        menubar: false,
        branding: false,
        browser_spellcheck: true,
        plugins: ['link', 'code'],
        toolbar: ['undo redo | bold | pagebreak | link | code'],
        style_formats: [
          { title: 'Paragraph', format: 'p' },
          { title: 'Heading 1', format: 'h1' },
          { title: 'Heading 2', format: 'h2' },
        ],
        pagebreak_split_block: false,
      };
    },

    getString,
    // Takes full location as an argument and returns the nested upload folder location
    buildFileLocation(location) {
      if (!location) {
        return null;
      }
      return `${process.env.VUE_APP_API_URL}${location.replace('api/library', 'library/download')}`;
    },

    deletePromotion() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: this.getString('module.library.delete.title.single'),
          ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
          cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
        })
        .then(() => {
          // delete call
          this.axios
            .delete(`promotion/${this.data.id}`)
            .then(() => {
              // Refresh data to see the result
              this.$q.notify({
                message: this.getString('upload.delete.success').replace(
                  '%file%',
                  this.getString('promotion'),
                ),
                position: 'top-right',
                timeout: 1000,
                color: 'positive',
              });
              this.$router.push('/promotion');
            })
            .catch((error) => {
              // Error
              console.warn(error);
            });
        })
        .catch(() => {
          console.warn('Not removed');
        });
    },

    requestParams() {
      const image = this.data.image && this.data.image.location
        && this.data.image.location.replace('api/', '');
      const projects = this.data.projects.map(project => project.id);
      const relations = this.data.relations.map(relation => relation.id);

      const requiredFields = {
        title: this.data.title,
        content: this.data.content,
        datetime_online: moment(this.data.datetime_online).format('YYYY-MM-DD HH:mm:ss'),
        image: image || null,
        projects,
        relations,
      };

      const optional = {};

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(optional)) {
        if (value === null || value === undefined) delete optional[key];
      }

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      return fields;
    },

    setModalContext() {
      this.imageModal = !this.imageModal;
    },
  },

  filters: {
    checkEmpty(value) {
      return value === null || value === '' ? '-' : value;
    },

    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  mounted() {
    if (this.$route.params.id) this.readOnly = true;

    EventBus.$on('file-select:promotion-detail', (data) => {
      if (data.type === 'image') {
        this.imageSelected = true;
        this.imgSrc = data.location;
        if (this.data.image) {
          this.data.image.location = data.location;
        } else {
          // Added in case of an incoming post where image is null
          this.data.image = { location: data.location };
        }
      }

      this.data.cover = data;

      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('chips-autocomplete:projects', (selection) => {
      this.data.projects = selection;
    });

    EventBus.$on('chips-autocomplete:relations', (selection) => {
      this.data.relations = selection;
    });
  },

  created() {
    if (this.$route.params.id) {
      // Fetch the selected module details by id.
      this.axios
        .get(`promotion/${this.$route.params.id}`)
        .then((response) => {
          // In the module detail page, if a given id is not found, we will always get a 200 OK response.
          if (!response.data.promotion) {
            // Redirect to modules overview
            this.$router.push('/promotion');
            return;
          }

          this.data = response.data.promotion;

          this.dataLoaded = true;
        })
        .catch((error) => {
          console.warn(error);

          // Notify if nothing found
          if (error.response.status === 404) {
            this.$q.notify({
              message: getString(`module.${this.module}.edit.error.nothingFound`),
              type: 'warning',
              timeout: 2000,
              position: 'top-right',
            });
          } else {
            // Throw general error notification
            this.$q.notify({
              message: getString('error.general'),
              position: 'top-right',
              timeout: 1000,
              type: 'warning',
            });
          }

          // Redirect to modules overview
          this.$router.push(`/${this.module}`);
        });
    } else {
      // Nothing to load
      this.dataLoaded = true;
    }
  },

  destroyed() {
    EventBus.$off('chips-autocomplete:projects', () => {});
    EventBus.$off('chips-autocomplete:relations', () => {});
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.data-title
  font-weight 700

.data-content
  word-break break-all

.image-preview.image {
  padding: 0;
  height: 200px;
  background-image: linear-gradient(45deg, #F3F3F3 25%, transparent 5%), linear-gradient(-45deg, #F3F3F3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F3F3 75%), linear-gradient(-45deg, transparent 75%, #F3F3F3 75%);
}

.centered {
  text-align: center;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.image-preview {
  display flex
  height: 40vh;
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
